'use client';

import type { Ref } from 'react';
import { useTranslations } from 'next-intl';

import type { SiteNavNodeView } from '@/components/site-page/SitePage.types';
import { SiteHeaderNode } from './SiteHeaderNode';
import { zoneClasses } from './SiteHeader.style';
import type { OpenBy } from './SiteHeader.types';

interface SiteHeaderZoneProps {
  zone: 'left' | 'center' | 'right';
  nodes: SiteNavNodeView[];
  /** At 768-1023 px CENTER items with an icon, except the first, shed their labels. */
  isWide: boolean;
  open: { id: string; by: OpenBy } | null;
  onOpenRequest: (id: string, by: OpenBy) => void;
  onCloseRequest: (id: string) => void;
  navRef?: Ref<HTMLElement>;
}

/** One desktop zone as a labelled `<nav>`. An empty zone renders nothing. */
export function SiteHeaderZone({
  zone,
  nodes,
  isWide,
  open,
  onOpenRequest,
  onCloseRequest,
  navRef,
}: SiteHeaderZoneProps) {
  const t = useTranslations('SiteHeader');
  if (nodes.length === 0) return null;
  return (
    <nav
      ref={navRef}
      className={zoneClasses}
      aria-label={t(`${zone}Nav` as 'leftNav' | 'centerNav' | 'rightNav')}
      data-cy={`site-header-zone-${zone}`}
    >
      {nodes.map((node, index) => (
        <SiteHeaderNode
          key={node.id}
          node={node}
          iconOnly={
            !node.label || (!isWide && zone === 'center' && index > 0 && node.icon !== null)
          }
          open={open?.id === node.id}
          openBy={open?.id === node.id ? open.by : null}
          onOpenRequest={onOpenRequest}
          onCloseRequest={onCloseRequest}
        />
      ))}
    </nav>
  );
}
